import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./TournamentsHubPage.module.css";

import PageWrapper from "../components/layout/PageWrapper";
import razLight from "../assets/images/raz/razlight2.png";

/* ================= HERO ================= */
import tournamentHero from "../assets/images/raz/tournamenthub-hero2.png";
import defaultTournamentHero from "../assets/images/tournaments/default-tournament.jpg";

/* ================= DATA ================= */
import { tournaments2026 } from "../data/tournamentMeta";
import {
  getInternationalCompetitions,
} from "../contracts/competitionRegistry";
import { getTournamentVisual } from "../data/tournamentVisuals";

/* ================= TYPES ================= */
type TournamentRow = {
  name: string;
  year: number;
  description: string;
  logo?: string;
  route: string;
};

export default function TournamentsHubPage() {
  const navigate = useNavigate();

  const competitionMap = useMemo(() => {
    const map = new Map(
      getInternationalCompetitions().map((c) => [
        c.conceptId,
        c,
      ])
    );

    return map;
  }, []);

  /* ==================================================
     MEN'S INTERNATIONAL TOURNAMENTS
     ================================================== */

  const mensTournaments: TournamentRow[] = useMemo(
    () =>
      tournaments2026
        .filter(
          (t) =>
            (t.gender === "men" ||
              t.gender === "mixed") &&
            t.type !== "domestic"
        )
        .map((t) => {
          const visual =
            getTournamentVisual(
              t.conceptId
            );

          return {
            name:
              competitionMap.get(
                t.conceptId
              )?.name ??
              t.name,

            year: t.year,

            description:
              t.heroSubtitle ??
              competitionMap.get(
                t.conceptId
              )?.name ??
              "International rugby competition",

            logo:
              visual?.heroImageMen ||
              visual?.heroImageWomen ||
              visual?.logo ||
              defaultTournamentHero,

            route: t.route,
          };
        }),
    [competitionMap]
  );

  /* ==================================================
     MEN'S DOMESTIC LEAGUES
     ================================================== */

  const mensDomesticLeagues: TournamentRow[] =
    useMemo(
      () =>
        tournaments2026
          .filter(
            (t) =>
              t.gender === "men" &&
              t.type === "domestic"
          )
          .map((t) => {
            const visual =
              getTournamentVisual(
                t.conceptId
              );

            return {
              name: t.name,

              year: t.year,

              description:
                t.heroSubtitle ??
                t.name,

              logo:
                visual?.heroImageMen ||
                visual?.heroImageWomen ||
                visual?.logo ||
                defaultTournamentHero,

              route: t.route,
            };
          }),
      []
    );

  /* ==================================================
     WOMEN'S TOURNAMENTS
     ================================================== */

  const womensTournaments: TournamentRow[] =
    useMemo(
      () =>
        tournaments2026
          .filter(
            (t) =>
              t.gender === "women" ||
              t.gender === "mixed"
          )
          .map((t) => {
            const visual =
              getTournamentVisual(
                t.conceptId
              );

            return {
              name:
                competitionMap.get(
                  t.conceptId
                )?.name ??
                t.name,

              year: t.year,

              description:
                t.heroSubtitle ??
                competitionMap.get(
                  t.conceptId
                )?.name ??
                "International rugby competition",

              logo:
                visual?.heroImageWomen ||
                visual?.heroImageMen ||
                visual?.logo ||
                defaultTournamentHero,

              route: t.route,
            };
          }),
      [competitionMap]
    );

  return (
    <PageWrapper imageUrl={razLight}>
      <main className={styles.page}>
        {/* ================= HERO ================= */}
        <header
          className={styles.hero}
          style={{
            backgroundImage: `url(${tournamentHero})`,
          }}
        >
          <div className={styles.heroContent}>
            <h1>Tournaments</h1>

            <p>
              International rugby competitions — shared identity,
              rivalry, and tradition.
            </p>
          </div>
        </header>

        {/* ================= MEN ================= */}
        <section className={styles.section}>
          <div
            className={styles.rankingsStrip}
            onClick={() =>
              navigate("/rankings/men")
            }
          >
            <div className={styles.rankingsText}>
              <span
                className={
                  styles.rankingsTitle
                }
              >
                International Standings
              </span>

              <span
                className={
                  styles.rankingsMain
                }
              >
                World Rankings — Men
              </span>
            </div>

            <span
              className={
                styles.rankingsArrow
              }
            >
              →
            </span>
          </div>

          <div
            className={
              styles.sectionHeader
            }
          >
            <div
              className={
                styles.headerRow
              }
            >
              <h2>
                Men's Tournaments
              </h2>

              <button
                className={
                  styles.viewAll
                }
                onClick={() =>
                  navigate(
                    "/tournaments/men"
                  )
                }
              >
                View all →
              </button>
            </div>

            <p>
              Historic competitions shaping
              the global men's game.
            </p>
          </div>

          <div className={styles.list}>
            {mensTournaments.map(
              (t) => (
                <div
                  key={`${t.name}-${t.year}`}
                  className={
                    styles.row
                  }
                  onClick={() =>
                    navigate(
                      t.route
                    )
                  }
                  style={{
                    cursor:
                      "pointer",
                  }}
                >
                  {t.logo && (
                    <img
                      src={t.logo}
                      alt={t.name}
                      className={
                        styles.logo
                      }
                    />
                  )}

                  <div
                    className={
                      styles.text
                    }
                  >
                    <h3>
                      {t.name}{" "}
                      {t.year}
                    </h3>

                    <p
                      className={
                        styles.subtext
                      }
                    >
                      {
                        t.description
                      }
                    </p>
                  </div>
                </div>
              )
            )}
          </div>

          {/* ================= DOMESTIC LEAGUES ================= */}
          {mensDomesticLeagues.length >
            0 && (
            <>
              <div
                className={
                  styles.sectionHeader
                }
                style={{
                  marginTop:
                    "2rem",
                }}
              >
                <div
                  className={
                    styles.headerRow
                  }
                >
                  <h2>
                    Domestic Leagues
                  </h2>
                </div>

                <p>
                  Elite domestic competitions
                  from around the rugby world.
                </p>
              </div>

              <div
                className={
                  styles.list
                }
              >
                {mensDomesticLeagues.map(
                  (t) => (
                    <div
                      key={`${t.name}-${t.year}`}
                      className={
                        styles.row
                      }
                      onClick={() =>
                        navigate(
                          t.route
                        )
                      }
                      style={{
                        cursor:
                          "pointer",
                      }}
                    >
                      {t.logo && (
                        <img
                          src={t.logo}
                          alt={t.name}
                          className={
                            styles.logo
                          }
                        />
                      )}

                      <div
                        className={
                          styles.text
                        }
                      >
                        <h3>
                          {t.name}{" "}
                          {t.year}
                        </h3>

                        <p
                          className={
                            styles.subtext
                          }
                        >
                          {
                            t.description
                          }
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </section>

        {/* ================= WOMEN ================= */}
        <section className={styles.section}>
          <div
            className={styles.rankingsStrip}
            onClick={() =>
              navigate(
                "/rankings/women"
              )
            }
          >
            <div
              className={
                styles.rankingsText
              }
            >
              <span
                className={
                  styles.rankingsTitle
                }
              >
                International Standings
              </span>

              <span
                className={
                  styles.rankingsMain
                }
              >
                World Rankings — Women
              </span>
            </div>

            <span
              className={
                styles.rankingsArrow
              }
            >
              →
            </span>
          </div>

          <div
            className={
              styles.sectionHeader
            }
          >
            <div
              className={
                styles.headerRow
              }
            >
              <h2>
                Women's Tournaments
              </h2>

              <button
                className={
                  styles.viewAll
                }
                onClick={() =>
                  navigate(
                    "/tournaments/women"
                  )
                }
              >
                View all →
              </button>
            </div>

            <p>
              Elite competitions defining
              women's international rugby.
            </p>
          </div>

          <div className={styles.list}>
            {womensTournaments.map(
              (t) => (
                <div
                  key={`${t.name}-${t.year}`}
                  className={
                    styles.row
                  }
                  onClick={() =>
                    navigate(
                      t.route
                    )
                  }
                  style={{
                    cursor:
                      "pointer",
                  }}
                >
                  {t.logo && (
                    <img
                      src={t.logo}
                      alt={t.name}
                      className={
                        styles.logo
                      }
                    />
                  )}

                  <div
                    className={
                      styles.text
                    }
                  >
                    <h3>
                      {t.name}{" "}
                      {t.year}
                    </h3>

                    <p
                      className={
                        styles.subtext
                      }
                    >
                      {
                        t.description
                      }
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </section>
      </main>
    </PageWrapper>
  );
}